/**
 * Zentrale API-Konfiguration für Development und Production
 * 
 * Features:
 * - Automatische Environment-Detection
 * - Modulare URL-Verwaltung (Ein-Zeilen-Änderung)
 * - Backward-Compatible mit bestehender API-Logik
 * - Type-Safe Image URL Generation
 */

// Environment Detection
const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;
const nodeEnv = import.meta.env.NODE_ENV;

// Helper to strip quotes from environment variables (Coolify bug workaround)
const stripQuotes = (value: string | undefined): string => {
  if (!value) return '';
  // Remove leading and trailing single/double quotes
  return value.replace(/^['"]|['"]$/g, '');
};

// Base API Configuration
export const API_CONFIG = {
  // Modulare Base URL - HIER können Sie mit einer Zeile alles ändern
  // Fallback auf leeren String wenn CMS nicht verfügbar - Frontend crasht nicht
  baseURL: stripQuotes(import.meta.env.VITE_STRAPI_URL) || '',
  
  // Environment Info (für Debugging)
  environment: {
    isDevelopment,
    isProduction,
    nodeEnv,
    strapiUrl: import.meta.env.VITE_STRAPI_URL,
    appApiUrl: import.meta.env.VITE_APP_API_URL
  },

  // Smart Image URL Helper - Erhält bestehende Logik
  getImageUrl: (imagePath: string | undefined | null): string => {
    if (!imagePath) return '';
    
    // Wenn bereits absolute URL, verwende sie direkt
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // Erstelle vollständige URL mit Base URL
    const baseUrl = API_CONFIG.baseURL;
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    
    return `${baseUrl}${cleanPath}`;
  },

  // Image Format Helper - Erhält Strapi-spezifische Logik
  getOptimizedImageUrl: (
    imageObject: any,
    preferredSize: 'thumbnail' | 'small' | 'medium' | 'large' = 'medium'
  ): string => {
    if (!imageObject) return '';

    // Handle verschiedene Strapi Image Strukturen
    let imageData;
    
    // Struktur: { data: { attributes: { ... } } }
    if (imageObject.data?.attributes) {
      imageData = imageObject.data.attributes;
    }
    // Struktur: { attributes: { ... } }
    else if (imageObject.attributes) {
      imageData = imageObject.attributes;
    }
    // Direkte Struktur
    else {
      imageData = imageObject;
    }

    if (!imageData) return '';

    // Bevorzugte Größe verwenden, fallback zur original URL
    const formats = imageData.formats || {};
    const imageUrl = formats[preferredSize]?.url || 
                     formats.medium?.url || 
                     formats.small?.url || 
                     formats.thumbnail?.url ||
                     imageData.url;

    return API_CONFIG.getImageUrl(imageUrl);
  },

  // Project Image Helper - Spezifisch für Immobilien-Projekte
  getProjectImageUrl: (
    projectImages: any[],
    preferredSize: 'thumbnail' | 'small' | 'medium' | 'large' = 'medium',
    index: number = 0
  ): string => {
    if (!projectImages || !Array.isArray(projectImages) || projectImages.length === 0) {
      return '';
    }

    const imageObject = projectImages[index];
    return API_CONFIG.getOptimizedImageUrl(imageObject, preferredSize);
  },

  // Debug Helper
  getDebugInfo: () => ({
    currentBaseURL: API_CONFIG.baseURL,
    environment: API_CONFIG.environment,
    availableEnvVars: {
      VITE_STRAPI_URL: import.meta.env.VITE_STRAPI_URL,
      VITE_APP_API_URL: import.meta.env.VITE_APP_API_URL,
      NODE_ENV: import.meta.env.NODE_ENV,
      DEV: import.meta.env.DEV,
      PROD: import.meta.env.PROD
    }
  })
};

// Typed Interfaces für bessere DX
export interface ImageFormats {
  thumbnail?: { url: string; width: number; height: number };
  small?: { url: string; width: number; height: number };
  medium?: { url: string; width: number; height: number };
  large?: { url: string; width: number; height: number };
}

export interface StrapiImage {
  id: number;
  attributes?: {
    url: string;
    formats?: ImageFormats;
    alternativeText?: string;
    caption?: string;
  };
  // Direkte Struktur (manchmal)
  url?: string;
  formats?: ImageFormats;
}

export interface ProjectImage {
  id: number;
  url: string;
  formats?: ImageFormats;
  attributes?: {
    url: string;
    formats?: ImageFormats;
  };
}

// Console Info für Development
if (isDevelopment) {
  console.log('🚀 API_CONFIG initialized:', API_CONFIG.getDebugInfo());
}

export default API_CONFIG;
